import React, { createContext, useState, useContext, useEffect } from 'react';

// Create language context
const LanguageContext = createContext();

// Language provider component
export const LanguageProvider = ({ children }) => {
  // Get saved language from localStorage or default to 'en'
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    return savedLanguage || 'en';
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', language);
  }, [language]);

  // Toggle between English and German
  const toggleLanguage = () => {
    setLanguage(prevLang => prevLang === 'en' ? 'de' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translations object
export const translations = {
  en: {
    // Header
    journalTitle: 'Daily Learning Journal',
    goodMorning: 'Good Morning',
    goodAfternoon: 'Good Afternoon',
    goodEvening: 'Good Evening',
    journalQuote: 'Every evening is an opportunity to reflect, learn, and grow',

    // Task Section
    eveningTasks: 'Daily Tasks',
    completed: 'completed',
    addTask: 'Add Task',
    taskPlaceholder: 'What would you like to accomplish today?',
    highPriority: 'High Priority',
    mediumPriority: 'Medium Priority',
    lowPriority: 'Low Priority',
    personal: 'Personal',
    work: 'Work',
    learning: 'Learning',
    health: 'Health',
    add: 'Add Task',
    cancel: 'Cancel',

    // Learning Section
    learningGoals: 'Learning Goals',
    addLearningGoal: 'Add Learning Goal',
    learningPlaceholder: 'What would you like to learn this evening?',
    skillDevelopment: 'Skill Development',
    reading: 'Reading',
    onlineCourse: 'Online Course',
    practice: 'Practice',
    minutes15: '15 minutes',
    minutes30: '30 minutes',
    minutes45: '45 minutes',
    hour1: '1 hour',
    hours15: '1.5 hours',
    addGoal: 'Add Goal',

    // Quote Card
    dailyInspiration: 'Daily Inspiration',

    // Reflection Section
    eveningReflection: 'Evening Reflection',
    reflectionArchive: 'Reflection Archive',
    gratitude: 'Gratitude',
    gratitudePlaceholder: 'What are you grateful for today?',
    keyLearnings: 'Key Learnings',
    learningsPlaceholder: 'What did you learn or discover today?',
    tomorrowFocus: 'Tomorrow\'s Focus',
    tomorrowPlaceholder: 'What will you focus on tomorrow?',
    reflectionQuote: 'Reflection is the beginning of wisdom and the pathway to growth.',
    saveReflection: 'Save Reflection',
    viewArchive: 'View Archive',
    currentReflection: 'Current Reflection',
    noSavedReflections: 'No saved reflections yet. Save your reflections to see them here.',
    reflectionSaved: 'Reflection saved successfully!',
    noContentToSave: 'Please add some content before saving.',
    entryCount: 'Entry {current} of {total}',
    noContent: 'No content for this field',

    // Routine Section
    dailyRoutines: 'Daily Routines',
    morningRoutine: 'Morning Routine',
    dayRoutine: 'Day Routine',
    eveningRoutine: 'Evening Routine',
    affirmation: 'Daily Affirmation',
    meditation: 'Meditation',
    dayPlan: 'Day Planning',
    dayReflection: 'Day Reflection',
    dailyLearning: 'Daily Learning Review',
    weekPlanCheck: 'Weekly Plan Check',
    tomorrowPlan: 'Tomorrow\'s Plan',
    resetDaily: 'Reset Daily',
    routineQuote: 'Consistency in small actions creates extraordinary results.',

    // Language switcher
    switchLanguage: 'Deutsch',
  },
  de: {
    // Header
    journalTitle: 'Tägliches Lernjournal',
    goodMorning: 'Guten Morgen',
    goodAfternoon: 'Guten Tag',
    goodEvening: 'Guten Abend',
    journalQuote: 'Jeder Abend ist eine Gelegenheit zum Nachdenken, Lernen und Wachsen',

    // Task Section
    eveningTasks: 'Tagesaufgaben',
    completed: 'erledigt',
    addTask: 'Aufgabe hinzufügen',
    taskPlaceholder: 'Was möchtest du heute erledigen?',
    highPriority: 'Hohe Priorität',
    mediumPriority: 'Mittlere Priorität',
    lowPriority: 'Niedrige Priorität',
    personal: 'Persönlich',
    work: 'Arbeit',
    learning: 'Lernen',
    health: 'Gesundheit',
    add: 'Hinzufügen',
    cancel: 'Abbrechen',

    // Learning Section
    learningGoals: 'Lernziele',
    addLearningGoal: 'Lernziel hinzufügen',
    learningPlaceholder: 'Was möchtest du heute Abend lernen?',
    skillDevelopment: 'Fähigkeitsentwicklung',
    reading: 'Lesen',
    onlineCourse: 'Online-Kurs',
    practice: 'Übung',
    minutes15: '15 Minuten',
    minutes30: '30 Minuten',
    minutes45: '45 Minuten',
    hour1: '1 Stunde',
    hours15: '1,5 Stunden',
    addGoal: 'Ziel hinzufügen',

    // Quote Card
    dailyInspiration: 'Tägliche Inspiration',

    // Reflection Section
    eveningReflection: 'Abendliche Reflexion',
    reflectionArchive: 'Reflexions-Archiv',
    gratitude: 'Dankbarkeit',
    gratitudePlaceholder: 'Wofür bist du heute dankbar?',
    keyLearnings: 'Wichtige Erkenntnisse',
    learningsPlaceholder: 'Was hast du heute gelernt oder entdeckt?',
    tomorrowFocus: 'Morgiger Fokus',
    tomorrowPlaceholder: 'Worauf wirst du dich morgen konzentrieren?',
    reflectionQuote: 'Reflexion ist der Anfang der Weisheit und der Weg zum Wachstum.',
    saveReflection: 'Reflexion speichern',
    viewArchive: 'Archiv anzeigen',
    currentReflection: 'Aktuelle Reflexion',
    noSavedReflections: 'Noch keine gespeicherten Reflexionen. Speichere deine Reflexionen, um sie hier zu sehen.',
    reflectionSaved: 'Reflexion erfolgreich gespeichert!',
    noContentToSave: 'Bitte füge Inhalte hinzu, bevor du speicherst.',
    entryCount: 'Eintrag {current} von {total}',
    noContent: 'Kein Inhalt für dieses Feld',

    // Routine Section
    dailyRoutines: 'Tägliche Routinen',
    morningRoutine: 'Morgenroutine',
    dayRoutine: 'Tagesroutine',
    eveningRoutine: 'Abendroutine',
    affirmation: 'Tägliche Affirmation',
    meditation: 'Meditation',
    dayPlan: 'Tagesplanung',
    dayReflection: 'Tagesreflexion',
    dailyLearning: 'Tägliches Lernen überprüfen',
    weekPlanCheck: 'Wochenplan prüfen',
    tomorrowPlan: 'Morgiger Plan',
    resetDaily: 'Täglich zurücksetzen',
    routineQuote: 'Beständigkeit in kleinen Handlungen schafft außergewöhnliche Ergebnisse.',

    // Language switcher
    switchLanguage: 'English',
  }
};